// pantry.js — SKU shape helpers + Open Food Facts barcode lookup.
// Outputs are DB-shaped: no client-generated ids, no created_at (Postgres handles those).

export const SHELF_LIFE_BY_CATEGORY = {
  produce: 7, protein: 3, dairy: 14, pantry: 365, frozen: 90, spice: 730, beverage: 180, other: 90,
};

export const STORAGE_BY_CATEGORY = {
  produce: "fridge", protein: "fridge", dairy: "fridge", pantry: "pantry",
  frozen: "freezer", spice: "pantry", beverage: "fridge", other: "pantry",
};

export const CATEGORY_META = {
  produce:  { label: "Produce",  color: "sage",      icon: "🥬" },
  protein:  { label: "Protein",  color: "ember",     icon: "🥩" },
  dairy:    { label: "Dairy",    color: "parchment", icon: "🥛" },
  pantry:   { label: "Pantry",   color: "ink",       icon: "🌾" },
  frozen:   { label: "Frozen",   color: "ink",       icon: "❄️" },
  spice:    { label: "Spice",    color: "ember",     icon: "🌶️" },
  beverage: { label: "Beverage", color: "sage",      icon: "🍶" },
  other:    { label: "Other",    color: "ink",       icon: "📦" },
};

// Lookup a barcode. Goes through our /api/barcode proxy (cache-first, proper
// User-Agent, remembers misses — decision G2). Falls back to a direct Open Food
// Facts call if the proxy isn't reachable (e.g. `vite dev` doesn't serve /api).
// Returns DB-shaped sku payload, or null.
export async function lookupBarcode(barcode) {
  try {
    const res = await fetch(`/api/barcode?code=${encodeURIComponent(barcode)}`);
    if (res.ok) {
      const data = await res.json();
      if (!data.found || !data.product) return null;
      return offToSku(data.product, barcode);
    }
    // 4xx from the proxy (bad barcode) is a definitive answer — don't retry OFF.
    if (res.status >= 400 && res.status < 500) return null;
    // 5xx / proxy unreachable → fall through to direct OFF.
  } catch (e) {
    console.warn("barcode proxy unreachable, falling back to OFF", e?.message);
  }
  return lookupBarcodeDirect(barcode);
}

// Direct Open Food Facts call. Fallback path only — prefer lookupBarcode().
async function lookupBarcodeDirect(barcode) {
  try {
    const res = await fetch(`https://world.openfoodfacts.org/api/v2/product/${barcode}.json`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.status !== 1) return null;
    return offToSku(data.product, barcode);
  } catch (e) {
    console.warn("OFF lookup failed", e);
    return null;
  }
}

function offToSku(p, barcode) {
  const category = inferCategory(p);
  const { quantity, unit } = parseQuantity(p.quantity || "");
  return {
    barcode,
    canonical_name: (p.product_name || "Unknown product").trim(),
    brand: p.brands || "",
    category,
    image_url: p.image_front_small_url || p.image_front_url || null,
    unit_default: unit || "each",
    pack_size: quantity || 1,
    pack_unit: unit || "each",
    shelf_life_days: SHELF_LIFE_BY_CATEGORY[category],
    storage: STORAGE_BY_CATEGORY[category],
    tags: (p.categories_tags || []).slice(0, 5).map(t => t.replace(/^en:/, "")),
    created_from: "scan",
  };
}

// Manual entry. Returns DB-shaped sku payload.
export function manualSku({ name, category = "other", brand = "", barcode = null }) {
  return {
    barcode,
    canonical_name: name,
    brand,
    category,
    image_url: null,
    unit_default: "each",
    pack_size: 1,
    pack_unit: "each",
    shelf_life_days: SHELF_LIFE_BY_CATEGORY[category],
    storage: STORAGE_BY_CATEGORY[category],
    tags: [],
    created_from: "manual",
  };
}

// Build an inventory row payload (DB will assign id + created_at).
export function newInventoryEntryForSku(sku, quantity = 1) {
  const purchased = new Date();
  const expires = new Date(purchased.getTime() + (sku.shelf_life_days || 90) * 86400000);
  return {
    sku_id: sku.id,
    quantity: quantity * (sku.pack_size || 1),
    unit: sku.unit_default,
    purchased_at: purchased.toISOString(),
    expires_at: expires.toISOString(),
    location: sku.storage,
  };
}

// Category-aware "use it or lose it" lead times. Perishables surface with MORE
// warning (you need days to actually cook the protein/produce before it turns);
// shelf-stable stuff only surfaces when it's right at the edge. Days until expiry
// <= the category's lead → it's "use soon". Falls back to `other` (3 days).
export const USE_SOON_LEAD_DAYS = {
  protein: 4, produce: 4, dairy: 3, beverage: 2, frozen: 2, pantry: 2, spice: 1, other: 3,
};

// Items that are expired or within their category's use-soon lead window.
// Unlike expiringWithin, this KEEPS negative days (already expired) and tags each
// with an urgency level so the UI can color/sort without re-deriving. Sorted most
// urgent first (expired → today → soonest).
export function useSoonItems(inventory, skus) {
  const now = Date.now();
  return (inventory || [])
    .filter((e) => e.expires_at)
    .map((e) => {
      const sku = (skus || []).find((s) => s.id === e.sku_id) || null;
      const category = sku?.category || "other";
      const lead = USE_SOON_LEAD_DAYS[category] ?? USE_SOON_LEAD_DAYS.other;
      const days = Math.floor((new Date(e.expires_at).getTime() - now) / 86400000);
      const urgency = days < 0 ? "expired" : days === 0 ? "today" : days <= 1 ? "tomorrow" : "soon";
      return { ...e, sku, category, lead, days_until_expiry: days, urgency };
    })
    .filter((e) => e.days_until_expiry <= e.lead)
    .sort((a, b) => a.days_until_expiry - b.days_until_expiry);
}

// Freezing resets the clock: move to the freezer and push expiry out by the
// frozen shelf life. Used by trip mode's "Freeze before you go" action.
export function freezePayload(now = new Date()) {
  const frozenDays = SHELF_LIFE_BY_CATEGORY.frozen || 90;
  return {
    location: "freezer",
    expires_at: new Date(now.getTime() + frozenDays * 86400000).toISOString(),
  };
}

export function expiringWithin(inventory, skusOrDays, daysArg) {
  // Polymorphic: (inventory, days) or (inventory, skus, days)
  const skus = Array.isArray(skusOrDays) ? skusOrDays : null;
  const days = skus ? daysArg : skusOrDays;
  const cutoff = Date.now() + days * 86400000;
  const now = Date.now();
  const filtered = inventory
    .filter(e => e.expires_at && new Date(e.expires_at).getTime() <= cutoff)
    .sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));
  if (!skus) return filtered;
  // Enrich with sku and days_until_expiry
  return filtered.map(e => {
    const sku = skus.find(s => s.id === e.sku_id) || null;
    const ms = new Date(e.expires_at).getTime() - now;
    return {
      ...e,
      sku,
      days_until_expiry: Math.max(0, Math.round(ms / 86400000)),
    };
  });
}

function inferCategory(p) {
  const cats = (p.categories_tags || []).join(" ").toLowerCase();
  if (cats.includes("frozen")) return "frozen";
  if (cats.includes("dairy") || cats.includes("milk") || cats.includes("cheese") || cats.includes("yogurt")) return "dairy";
  if (cats.includes("meat") || cats.includes("fish") || cats.includes("seafood") || cats.includes("poultry")) return "protein";
  if (cats.includes("vegetable") || cats.includes("fruit") || cats.includes("produce")) return "produce";
  if (cats.includes("spice") || cats.includes("herb") || cats.includes("seasoning")) return "spice";
  if (cats.includes("beverage") || cats.includes("drink")) return "beverage";
  return "pantry";
}

function parseQuantity(q) {
  const m = q.match(/(\d+(?:[.,]\d+)?)\s*(g|kg|ml|l|oz|lb|fl\s*oz)/i);
  if (!m) return { quantity: null, unit: null };
  return {
    quantity: parseFloat(m[1].replace(",", ".")),
    unit: m[2].toLowerCase().replace(/\s+/g, ""),
  };
}
